import { useState, useCallback, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Recycle, Mail, Lock, Eye, EyeOff } from "lucide-react";
import api from "../lib/Urls";
import { registerFcmTokenInBackground } from "../lib/notificationRegistration";
import GoogleSignInButton from "../components/GoogleSignInButton";
import { getStoredToken, saveAuthSession } from "../lib/authStorage";
import { syncCurrentLocationAfterAuth } from "../lib/locationSync";

const SignIn = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const isMounted = useRef(true);
  const loginCache = useRef(false);

  useEffect(() => {
    isMounted.current = true;

    const token = getStoredToken();

    if (token) {
      navigate("/", { replace: true });
    }

    return () => {
      isMounted.current = false;
    };
  }, [navigate]);

  const saveSessionAndContinue = useCallback(
    async (user) => {
      saveAuthSession(user);

      // Browser push registration continues independently of navigation.
      registerFcmTokenInBackground(user.userId);

      // Location is optional and syncs in the background. A denied/failed
      // location request never turns a successful authentication into a failed login.
      syncCurrentLocationAfterAuth();

      if (!isMounted.current) return;

      navigate("/", { replace: true });
    },
    [navigate]
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      setError("");

      if (loginCache.current) return;

      loginCache.current = true;
      setLoading(true);

      try {
        const res = await api.post("/api/users/login", {
          email: email.trim(),
          password,
        });

        await saveSessionAndContinue(res.data);
      } catch (err) {
        if (!isMounted.current) return;

        setError(
          err.response?.data?.message ||
            err.message ||
            "Unable to sign in. Please check your credentials."
        );
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }

        loginCache.current = false;
      }
    },
    [email, password, saveSessionAndContinue]
  );

  const handleGoogle = useCallback(
    async (credentialResponse) => {
      setError("");

      if (loginCache.current) return;

      const credential = credentialResponse?.credential;

      if (!credential) {
        setError(
          "Google did not return a valid sign-in credential."
        );
        return;
      }

      loginCache.current = true;
      setLoading(true);

      try {
        const res = await api.post("/api/users/google-login", {
          credential,
        });

        await saveSessionAndContinue(res.data);
      } catch (err) {
        if (!isMounted.current) return;

        setError(
          err.response?.data?.message ||
            err.message ||
            "Unable to sign in with Google."
        );
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }

        loginCache.current = false;
      }
    },
    [saveSessionAndContinue]
  );

  const handleGoogleLoadError = useCallback((loadError) => {
    console.error("Google sign-in setup failed:", loadError);

    if (isMounted.current) {
      setError(
        loadError?.message ||
          "Google sign-in is currently unavailable."
      );
    }
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="mb-8 flex items-center justify-center gap-2"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
            <Recycle className="h-6 w-6 text-primary-foreground" />
          </div>

          <span className="font-heading text-2xl font-bold text-foreground">
            Deal<span className="text-primary">Swapify</span>
          </span>
        </Link>

        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="mb-1 font-heading text-xl font-bold text-foreground">
            Welcome Back
          </h2>

          <p className="mb-6 text-sm text-muted-foreground">
            Sign in to your DealSwapify account.
          </p>

          {error && (
            <div
              role="alert"
              className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />

              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                disabled={loading}
                className="w-full rounded-lg border border-border bg-secondary py-2.5 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60"
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />

              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                disabled={loading}
                className="w-full rounded-lg border border-border bg-secondary py-2.5 pl-10 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60"
              />

              <button
                type="button"
                onClick={() =>
                  setShowPassword((current) => !current)
                }
                disabled={loading}
                aria-label={
                  showPassword
                    ? "Hide password"
                    : "Show password"
                }
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>

            <div className="text-right">
              <Link
                to="/reset-password"
                className="text-xs text-primary hover:underline"
              >
                Forgot Password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:shadow-[var(--glow-primary)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground">
              or
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <GoogleSignInButton
            onCredential={handleGoogle}
            onLoadError={handleGoogleLoadError}
            disabled={loading}
          />

          <p className="mt-3 text-center text-[10px] text-muted-foreground">
            Location lookup data © OpenStreetMap contributors.
          </p>

          <div className="mt-5 text-center text-sm text-muted-foreground">
            Don't have an account?{" "}
            <Link
              to="/signup"
              className="text-primary hover:underline"
            >
              Sign Up
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
